import { useEffect, useState } from 'react';

export function useCachedVideo(url?: string) {
    const [cachedUrl, setCachedUrl] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [mimeType, setMimeType] = useState<string | null>(null);

    useEffect(() => {
        if (!url) {
            return;
        }

        const controller = new AbortController();

        setError(null);
        setLoading(true);

        (async () => {

            try {

                // The fetched video.
                const response = await fetch(url, {
                    headers: {
                        'x-force-video-cache': 'true'
                    },
                    signal: controller.signal
                });

                if (!response.ok) {
                    setError(Error(`Failed to fetch video (status: ${response.status})`));
                    return;
                }

                const contentType = response.headers.get('Content-Type');

                setMimeType(contentType);

                let blob: Blob;

                if (response.body?.getReader) {

                    const reader = response.body.getReader();
                    const chunks: BlobPart[] = [];

                    // Consumes the video entirely.
                    while (true) {

                        // The next chunk.
                        const next = await reader.read();

                        if (!next || next.done) {
                            break;
                        }

                        if (next.value) {
                            chunks.push(next.value);
                        }

                    }

                    blob = new Blob(chunks, {
                        type: contentType ?? undefined,
                    });

                } else {

                    blob = await response.blob();

                }

                const blobUrl = URL.createObjectURL(blob);

                setCachedUrl(blobUrl);

            } catch (err) {

                if (err instanceof DOMException && err.name === 'AbortError') {
                    return;
                }

                setError(err instanceof Error
                    ? err :
                    new Error(String(err))
                );

            } finally {

                if (!controller.signal.aborted) {
                    setLoading(false);
                }

            }
        })();

        // Cleanup on unmount
        return () => {
            controller.abort();
        };
    }, [url]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cachedUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(cachedUrl);
            }
        };
    }, [cachedUrl]);

    return {
        cachedUrl,
        error,
        loading,
        mimeType,
    };
}
